import { filter, forEach, map, reduce, sortBy } from "lodash";
import moment, { unitOfTime } from "moment";

import {
  AddressValidation,
  BulkyItem,
  BulkyItemSchedulerCitySettings,
  ScheduledPickupOnAddress,
  SchedulerWizardFormValues,
  SeasonalSetting,
} from "src/interfaces";
import {
  DAY_PERIOD_TYPE_ID,
  FIRST_TYPE_ID,
  MAX_SEARCHABLE_MONTHS,
  MONTH_PERIOD_TYPE_ID,
  QUARTER_PERIOD_TYPE_ID,
  WEEK_PERIOD_TYPE_ID,
  YEAR_PERIOD_TYPE_ID,
} from "src/constants";
import { getItemSettings } from "./bulkyItems";
import { PickupsInZone } from "src/interfaces/schedulerWizard";
import { TimeRangeList } from "src/interfaces/citySettings";

interface DateInterval {
  startDate: Date;
  endDate: Date;
}

const normalizeInterval = (interval: DateInterval[]) => {
  if (interval.length === 0) {
    return [];
  }
  if (interval.length === 1) {
    return interval;
  }

  const sortedByStartDate = sortBy(interval, "startDate");
  let mergedIntervals = [sortedByStartDate[0]];

  for (let i = 0; i < sortedByStartDate.length; i++) {
    const lastMergedInterval = mergedIntervals[mergedIntervals.length - 1];
    const currentInterval = sortedByStartDate[i];

    const lastEndDate = moment(lastMergedInterval.endDate);
    const currentStartDate = moment(currentInterval.startDate);
    const currentEndDate = moment(currentInterval.endDate);

    if (currentStartDate.isBefore(lastEndDate) && currentEndDate.isAfter(lastEndDate)) {
      lastMergedInterval.endDate = currentInterval.endDate;
    } else if (currentStartDate.isAfter(lastEndDate)) {
      mergedIntervals.push(currentInterval);
    }
  }
  return mergedIntervals;
};

const getIntervalDate = (
  year: number,
  month: number,
  dayOfWeek: number,
  dayTypeId: number,
  isEndDate?: boolean
) => {
  const startOfMonth = moment().year(year).month(month).startOf("month");
  const endOfMonth = moment().year(year).month(month).endOf("month");

  const monthStartDayOfWeek = startOfMonth.get("day");
  const monthEndDayOfWeek = endOfMonth.get("day");

  const dayToAdd = dayOfWeek >= monthStartDayOfWeek ? 0 : 7;
  const dayToSubtract = dayOfWeek <= monthEndDayOfWeek ? 0 : 7;

  const date =
    dayTypeId === FIRST_TYPE_ID
      ? startOfMonth.add(dayToAdd, "days")
      : endOfMonth.subtract(dayToSubtract, "days");

  return isEndDate
    ? date.day(dayOfWeek).endOf("day").toDate()
    : date.day(dayOfWeek).startOf("day").toDate();
};

export const seasonalityAndBlackoutDateRangeMatcher = (
  seasonality: SeasonalSetting[],
  isBlackout?: boolean,
  customMaxFutureSearchableMonths?: number
) => {
  return (day: Date) => {
    if (!day) return true;

    // generate date intervals form seasonal settings
    const dateIntervals = map(
      seasonality,
      ({
        descriptiveBeginMonth,
        descriptiveEndMonth,
        descriptiveBeginDayOfWeek,
        descriptiveEndDayOfWeek,
        descriptiveBeginDayTypeId,
        descriptiveEndDayTypeId,
        specificBeginDate,
        specificEndDate,
        singleDate,
      }) => {
        if (singleDate) {
          // is single date
          const startDate = moment(singleDate).startOf("day").toDate();
          const endDate = moment(singleDate).endOf("day").toDate();

          return { startDate, endDate };
        } else if (
          descriptiveBeginDayOfWeek &&
          descriptiveBeginDayTypeId &&
          descriptiveEndDayOfWeek &&
          descriptiveEndDayTypeId &&
          descriptiveBeginMonth &&
          descriptiveEndMonth
        ) {
          // is descriptive date range
          const date = moment(day);
          const year = date.year();
          const startMonthForMoment = descriptiveBeginMonth - 1;
          const endMonthForMoment = descriptiveEndMonth - 1;

          let startDate = getIntervalDate(
            year,
            startMonthForMoment,
            descriptiveBeginDayOfWeek,
            descriptiveBeginDayTypeId
          );
          let endDate = getIntervalDate(
            year,
            endMonthForMoment,
            descriptiveEndDayOfWeek,
            descriptiveEndDayTypeId,
            true
          );

          return { startDate, endDate };
        } else if (specificBeginDate) {
          // is specific date range
          const startDate = moment(specificBeginDate).startOf("day").toDate();
          const endDate = specificEndDate
            ? moment(specificEndDate).endOf("day").toDate()
            : moment(specificBeginDate)
                .add(customMaxFutureSearchableMonths || MAX_SEARCHABLE_MONTHS, "months")
                .endOf("day")
                .toDate();

          return { startDate, endDate };
        }
      }
    ) as DateInterval[];

    // combines overlapping intervals
    const normalizedIntervals = normalizeInterval(dateIntervals.filter((intervall) => !!intervall));

    return (
      filter(normalizedIntervals, ({ startDate, endDate }) => {
        const date = moment(day);
        return isBlackout
          ? !date.isBetween(startDate, endDate, undefined, "[]")
          : date.isBetween(startDate, endDate, undefined, "[]");
      }).length === 0
    );
  };
};

export const dayDisabledMatcher = (
  citySettings: BulkyItemSchedulerCitySettings,
  addressValidation: AddressValidation,
  currentItem: BulkyItem,
  currentSchedulerData: SchedulerWizardFormValues,
  pickupsInZone: PickupsInZone[]
) => {
  return (day: Date) => {
    let shouldDisableDay = false;

    /*
        MAX PICKUPS PER DAY VALIDATION
    */
    if (citySettings.categories?.length && citySettings.categories[0].dayOfWeekList?.length) {
      let scheduledOnThisDay = 0;
      pickupsInZone.forEach((pickupInZone) => {
        if (moment(pickupInZone.pickupDate).isSame(day, "day")) {
          scheduledOnThisDay = scheduledOnThisDay + pickupInZone.currentScheduledPickupCount;
        }
      });

      scheduledOnThisDay =
        scheduledOnThisDay +
        filter(currentSchedulerData.items, (item) => {
          return (
            moment(item.pickupDate).isSame(day, "day") &&
            item.bulkyCategoryTypeId === currentItem.bulkyCategoryTypeId
          );
        }).length;

      // max pickups are based on the day of the week and the custom/default settings for the category
      const itemMaxPickupsPerDay = getItemSettings(
        currentItem,
        typeof day.toISOString === "function" ? day.toISOString() : "",
        citySettings.categories,
        addressValidation
      ).maxPickups;

      if (!shouldDisableDay && itemMaxPickupsPerDay && scheduledOnThisDay >= itemMaxPickupsPerDay) {
        shouldDisableDay = true;
      }
    }

    /*
        MAX PICKUPS PER LOCATION IN PERIOD TYPE VALIDATION
    */
    if (
      addressValidation.scheduledPickupsSummary?.scheduledPickups?.length &&
      citySettings.vendorSettings?.serviceLocationThresholdSettings?.length
    ) {
      const scheduledPickups = addressValidation.scheduledPickupsSummary.scheduledPickups;

      const serviceLocationThresholdSettings =
        citySettings.vendorSettings.serviceLocationThresholdSettings;

      forEach(serviceLocationThresholdSettings, (thresholdSetting) => {
        switch (thresholdSetting.periodTypeId) {
          case DAY_PERIOD_TYPE_ID:
            const shouldDisableBasedOnDay = shouldDisableThisDayBasedOnPeriod(
              scheduledPickups,
              currentSchedulerData,
              day,
              "day",
              thresholdSetting.maxLocationPickups
            );
            if (!shouldDisableDay && shouldDisableBasedOnDay) shouldDisableDay = true;
            break;

          case WEEK_PERIOD_TYPE_ID:
            const shouldDisableBasedOnWeek = shouldDisableThisDayBasedOnPeriod(
              scheduledPickups,
              currentSchedulerData,
              day,
              "week",
              thresholdSetting.maxLocationPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnWeek) shouldDisableDay = true;

            break;

          case MONTH_PERIOD_TYPE_ID:
            const shouldDisableBasedOnMonth = shouldDisableThisDayBasedOnPeriod(
              scheduledPickups,
              currentSchedulerData,
              day,
              "month",
              thresholdSetting.maxLocationPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnMonth) shouldDisableDay = true;

            break;

          case QUARTER_PERIOD_TYPE_ID:
            const shouldDisableBasedOnQuarter = shouldDisableThisDayBasedOnPeriod(
              scheduledPickups,
              currentSchedulerData,
              day,
              "quarter",
              thresholdSetting.maxLocationPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnQuarter) shouldDisableDay = true;

            break;

          case YEAR_PERIOD_TYPE_ID:
            const shouldDisableBasedOnYear = shouldDisableThisDayBasedOnPeriod(
              scheduledPickups,
              currentSchedulerData,
              day,
              "year",
              thresholdSetting.maxLocationPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnYear) shouldDisableDay = true;

            break;
        }
      });
    }

    /*
        MAX PICKUPS PER TIME RANGE
    */
    if (citySettings.categories?.length && citySettings.categories[0].timeRangeList?.length) {
      const categoriesWithZoneId = citySettings.categories[0]?.timeRangeList?.filter(
        (timeRange: TimeRangeList) =>
          addressValidation?.zoneIds?.some((zoneId) => timeRange?.zoneIds?.includes(zoneId))
      );

      const categoriesWithZoneIdFiltered = Object.values(
        categoriesWithZoneId?.reduce((acc, curr) => {
          if (
            !acc[curr.periodTypeId as number] ||
            curr.maxPickups > acc[curr.periodTypeId as number].maxPickups
          ) {
            acc[curr.periodTypeId as number] = curr;
          }
          return acc;
        }, {} as TimeRangeList as any)
      ) as TimeRangeList[];

      const categoriesWithoutZoneId = citySettings.categories[0]?.timeRangeList?.filter(
        (timeRange: TimeRangeList) => !timeRange.zoneIds.length
      );

      const currentCategory = !!categoriesWithZoneIdFiltered?.length
        ? categoriesWithZoneIdFiltered
        : categoriesWithoutZoneId;

      forEach(currentCategory, (timeRange) => {
        switch (timeRange.periodTypeId) {
          case DAY_PERIOD_TYPE_ID:
            const shouldDisableBasedOnDay = shouldDisableThisDayBasedOnPeriodAndPickupsInZone(
              pickupsInZone,
              currentSchedulerData,
              day,
              "day",
              timeRange.maxPickups
            );
            if (!shouldDisableDay && shouldDisableBasedOnDay) shouldDisableDay = true;
            break;

          case WEEK_PERIOD_TYPE_ID:
            const shouldDisableBasedOnWeek = shouldDisableThisDayBasedOnPeriodAndPickupsInZone(
              pickupsInZone,
              currentSchedulerData,
              day,
              "week",
              timeRange.maxPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnWeek) shouldDisableDay = true;

            break;

          case MONTH_PERIOD_TYPE_ID:
            const shouldDisableBasedOnMonth = shouldDisableThisDayBasedOnPeriodAndPickupsInZone(
              pickupsInZone,
              currentSchedulerData,
              day,
              "month",
              timeRange.maxPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnMonth) shouldDisableDay = true;

            break;

          case QUARTER_PERIOD_TYPE_ID:
            const shouldDisableBasedOnQuarter = shouldDisableThisDayBasedOnPeriodAndPickupsInZone(
              pickupsInZone,
              currentSchedulerData,
              day,
              "quarter",
              timeRange.maxPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnQuarter) shouldDisableDay = true;

            break;

          case YEAR_PERIOD_TYPE_ID:
            const shouldDisableBasedOnYear = shouldDisableThisDayBasedOnPeriodAndPickupsInZone(
              pickupsInZone,
              currentSchedulerData,
              day,
              "year",
              timeRange.maxPickups
            );

            if (!shouldDisableDay && shouldDisableBasedOnYear) shouldDisableDay = true;

            break;
        }
      });
    }

    return shouldDisableDay;
  };
};

const shouldDisableThisDayBasedOnPeriod = (
  scheduledPickups: ScheduledPickupOnAddress[],
  currentSchedulerData: SchedulerWizardFormValues,
  day: Date,
  periodType: unitOfTime.StartOf,
  thresholdMaxPickups: number
) => {
  const scheduledPickupsOnThisPeriod = filter(scheduledPickups, (scheduledPickup) =>
    moment(scheduledPickup.pickupDate).isSame(day, periodType)
  );

  const scheduledPickupsOnThisPeriodCount = reduce(
    scheduledPickupsOnThisPeriod,
    (sum, scheduledPickup) => sum + scheduledPickup.totalPickups,
    0
  );

  const scheduledPickupsOnThisPeriodCountWithCurrentItem = reduce(
    filter(currentSchedulerData.items, (item) => moment(item.pickupDate).isSame(day, periodType)),
    (sum) => sum++,
    scheduledPickupsOnThisPeriodCount
  );

  return scheduledPickupsOnThisPeriodCountWithCurrentItem >= thresholdMaxPickups;
};

const shouldDisableThisDayBasedOnPeriodAndPickupsInZone = (
  pickupsInZone: PickupsInZone[],
  currentSchedulerData: SchedulerWizardFormValues,
  day: Date,
  periodType: unitOfTime.StartOf,
  thresholdMaxPickups: number
) => {
  const scheduledPickupsOnThisPeriod = filter(pickupsInZone, (pickupInZone) =>
    moment(pickupInZone.pickupDate).isSame(day, periodType)
  );

  const scheduledPickupsOnThisPeriodCount = reduce(
    scheduledPickupsOnThisPeriod,
    (sum, pickupInZone) => sum + pickupInZone.currentScheduledPickupCount,
    0
  );

  const scheduledPickupsOnThisPeriodCountWithCurrentItem = reduce(
    filter(currentSchedulerData.items, (item) => moment(item.pickupDate).isSame(day, periodType)),
    (sum) => sum++,
    scheduledPickupsOnThisPeriodCount
  );

  return scheduledPickupsOnThisPeriodCountWithCurrentItem >= thresholdMaxPickups;
};
